/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect } from "react";

// react-router-dom components
import { Link, useNavigate } from "react-router-dom";

// @mui material components
import Card from "@mui/material/Card";
import Switch from "@mui/material/Switch";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";

// Authentication layout components
import BasicLayout from "layouts/authentication/components/BasicLayout";

// Images
import bgImage from "assets/images/photo_2025-10-14_14-05-23.jpg";

// Services - API Authentication
import { authenticateUser } from "services/authServiceAPI";

// Material Dashboard 2 React context
import {
  useMaterialUIController,
  setLayout,
  setMiniSidenav,
  setDarkMode,
  setSidenavColor,
  setTransparentSidenav,
  setWhiteSidenav,
  setFixedNavbar
} from "context";

function Basic() {
  const [controller, dispatch] = useMaterialUIController();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    // Réinitialiser le thème aux valeurs par défaut
    setLayout(dispatch, "page");
    setDarkMode(dispatch, false);
    setSidenavColor(dispatch, "info");
    setTransparentSidenav(dispatch, true);
    setWhiteSidenav(dispatch, false);
    setFixedNavbar(dispatch, true);
  }, [dispatch]);

  const handleSetRememberMe = () => setRememberMe(!rememberMe);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      // Authentification avec MongoDB
      const result = await authenticateUser(email, password);

      if (result.success) {
        // Sauvegarder les infos rememberMe si nécessaire
        if (rememberMe) {
          localStorage.setItem("rememberMe", "true");
        }

        console.log("✅ Connexion réussie, redirection vers le dashboard");

        // NE PAS arrêter le loading, garder le spinner pendant la transition
        // setLoading reste à true

        // Petit délai pour éviter le flash du menu pendant la transition
        setTimeout(() => {
          // Forcer le layout à "dashboard" ET fermer le Sidenav (miniSidenav = true)
          setLayout(dispatch, "dashboard");
          setMiniSidenav(dispatch, true); // Fermer le menu sur mobile

          // Naviguer vers le dashboard (le loading s'arrêtera là-bas)
          navigate("/dashboard", { replace: true });
        }, 100);
      } else {
        setError(result.message);
        setLoading(false);
      }
    } catch (err) {
      console.error("Erreur lors de la connexion:", err);
      setError("Erreur de connexion. Veuillez réessayer.");
      setLoading(false);
    }
  };

  return (
    <BasicLayout image={null}>
      <Card>
        <MDBox
          sx={{
            width: "100%",
            height: "200px",
            backgroundImage: `url(${bgImage})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            borderRadius: "8px 8px 0 0",
          }}
        />
        <MDBox pt={4} pb={3} px={3}>
          <MDBox component="form" role="form" onSubmit={handleSubmit}>
            {error && (
              <MDBox mb={2}>
                <Alert severity="error">{error}</Alert>
              </MDBox>
            )}
            <MDBox mb={2}>
              <MDInput
                type="email"
                label="Email"
                fullWidth
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={loading}
                InputLabelProps={{ shrink: true }}
              />
            </MDBox>
            <MDBox mb={2}>
              <MDInput
                type="password"
                label="Mot de passe"
                fullWidth
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={loading}
                InputLabelProps={{ shrink: true }}
              />
            </MDBox>
            <MDBox display="flex" alignItems="center" ml={-1}>
              <Switch checked={rememberMe} onChange={handleSetRememberMe} disabled={loading} />
              <MDTypography
                variant="button"
                fontWeight="regular"
                color="text"
                onClick={handleSetRememberMe}
                sx={{ cursor: "pointer", userSelect: "none", ml: -1 }}
              >
                &nbsp;&nbsp;Se souvenir de moi
              </MDTypography>
            </MDBox>
            <MDBox mt={4} mb={1}>
              <MDButton variant="gradient" color="info" fullWidth type="submit" disabled={loading}>
                {loading ? <CircularProgress size={24} color="inherit" /> : "Se connecter"}
              </MDButton>
            </MDBox>
            <MDBox mt={3} mb={1} textAlign="center">
              <MDTypography variant="button" color="text">
                Vous n&apos;avez pas de compte?{" "}
                <MDTypography
                  component={Link}
                  to="/authentication/sign-up"
                  variant="button"
                  color="info"
                  fontWeight="medium"
                  textGradient
                >
                  S&apos;inscrire
                </MDTypography>
              </MDTypography>
            </MDBox>
          </MDBox>
        </MDBox>
      </Card>
    </BasicLayout>
  );
}

export default Basic;
